import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import moment from 'moment';

import { DATA_TABLE_SOURCE_PURCHASE_REQUESTS } from 'common/dataTableConstants';

import {
  createFormValidationFn,
  createInitialValues,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import log from 'client/lib/log';
import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import {
  accountingDashboardHeaders,
  makeAccountingDashboardRows,
} from 'client/components/UPAdminAccountingDashboard/accountingDashboardRows';

import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrDataTableUI from 'client/components/SrDataTableUI';
import { SR_TABLE_THEME_PARTNER, SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_THEME_ADOBE_GREY,
} from 'client/components/SrButton/SrButton';
import SrLink from 'client/components/SrLink/SrLink';

const getFormFields = ({ onSearchChange, onLeaseStartDateChange }) => {
  return [
    {
      id: 'searchTerm',
      type: 'short_text',
      properties: {
        placeholder: 'Search Dealer Portal, Provider, Renter',
        onChange: onSearchChange,
      },
    },
    {
      id: 'leaseStartDate',
      type: 'date_picker',
      properties: {
        placeholder: 'Move In Date',
        dateFormat: 'MM/DD/YYYY',
        readOnly: false,
        onChange: onLeaseStartDateChange,
      },
    },
    {
      id: 'tableTheme',
      type: 'select',
      properties: {
        choices: [
          {
            value: SR_TABLE_THEME_PARTNER_CLEAR,
            label: 'Expanded view',
          },
          {
            value: SR_TABLE_THEME_PARTNER,
            label: 'Compact view',
          },
        ],
      },
    },
  ];
};

const UPAdminAccountingDashboard = ({ utilityDealers, utilitySalesReps }) => {
  const [isUpdatingItem, setIsUpdatingItem] = useState(false);
  const [updateModalProps, setUpdateModalProps] = useState(null);
  const [highlightRowId, setHighlightRowId] = useState(null);

  const dispatch = useDispatch();

  const closeModal = () => {
    setUpdateModalProps(null);
  };

  return (
    <div>
      <Formik
        initialValues={{
          searchTerm: '',
          tableTheme: SR_TABLE_THEME_PARTNER_CLEAR,
        }}
      >
        {(formikBag) => {
          return (
            <SrDataTableConnected
              dataSource={DATA_TABLE_SOURCE_PURCHASE_REQUESTS}
              initialViewSettings={{
                sort: {
                  accountRequestedAt: 'desc',
                },
              }}
              count={20}
              itemsPropName="rows"
              resetOnUnmount
            >
              {({
                rows,
                reloadItems,
                viewSettings,
                dataTableApiState,
                onInfiniteScrollWrapperRef,
              }) => {
                const formFields = getFormFields({
                  onSearchChange: (event) => {
                    const searchTerm = event.target?.value || '';
                    formikBag.setFieldValue('searchTerm', searchTerm);

                    viewSettings.update({
                      ...viewSettings,
                      fuzzy: !searchTerm
                        ? null
                        : {
                            queryString: searchTerm,
                            columns: ['providerName', 'dealerName', 'renterName', 'renterNumber'],
                          },
                    });
                  },
                  onLeaseStartDateChange: (date) => {
                    formikBag.setFieldValue('leaseStartDate', date);
                    viewSettings.update({
                      ...viewSettings,
                      filter: {
                        ...viewSettings.filter,
                        leaseStartDate: moment(date).format('YYYY-MM-DD'),
                      },
                    });
                  },
                });
                const getFormFieldsById = createFormFieldsByIdGetter(formFields);

                const updateActivationItem = ({ actionType, itemId, payload }) => {
                  setIsUpdatingItem(true);
                  dispatch(requestUtilityApi, {
                    action: actionType,
                    payload,
                    onSuccess() {
                      reloadItems({
                        count: 1,
                        filter: {
                          utilityActivationItemId: itemId,
                        },
                      }).then(() => {
                        setIsUpdatingItem(false);
                        closeModal();
                      });
                    },
                    onError(error) {
                      global.alert(error.stack || error.message);
                      log.error(error);

                      reloadItems({
                        count: 1,
                        filter: {
                          utilityActivationItemId: itemId,
                        },
                      }).then(() => {
                        setIsUpdatingItem(false);
                        closeModal();
                      });
                    },
                  });
                };

                const processedRows = makeAccountingDashboardRows({
                  rows,
                  updateActivationItem,
                  onEditableClick: setUpdateModalProps,
                  utilityDealers,
                  utilitySalesReps,
                  highlightRowId,
                  setHighlightRowId,
                });

                return (
                  <div ref={onInfiniteScrollWrapperRef}>
                    <SrBox paddingBottom="20px">
                      <SrFlex gap="20px" align="baseline">
                        <SrText font="greycliff" weight="700" fontSize="28px" lineHeight="34px">
                          Accounting Dashboard
                        </SrText>
                        <SrBox width="280px">
                          <SrFormikField
                            formField={getFormFieldsById('searchTerm')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox width="180px">
                          <SrFormikField
                            formField={getFormFieldsById('leaseStartDate')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox width="180px">
                          <SrFormikField
                            formField={getFormFieldsById('tableTheme')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox marginLeft="auto">
                          <SrLink
                            to="/utility-profit/admin/dashboard/accounting-non-internet"
                            target="_blank"
                          >
                            <SrText
                              font="greycliff"
                              color="blue300"
                              cursor="pointer"
                              weight="800"
                              fontSize="16px"
                              lineHeight="50px"
                            >
                              Go to Non-Internet Accounting Dashboard
                            </SrText>
                          </SrLink>
                        </SrBox>
                      </SrFlex>
                    </SrBox>
                    <SrDataTableUI
                      theme={formikBag.values.tableTheme}
                      className="tableAccountDashboard"
                      dataTableViewSettings={viewSettings}
                      headers={accountingDashboardHeaders}
                      rows={processedRows}
                      sticky
                    />
                    {dataTableApiState.inProgress && <div className="loading-ellipses" />}
                  </div>
                );
              }}
            </SrDataTableConnected>
          );
        }}
      </Formik>
      {updateModalProps && (
        <SrDarkModal>
          <SrModalContent onCloseIconClick={closeModal}>
            <Formik
              validate={(values) => {
                const formFields = updateModalProps.getFormFields(values);
                return createFormValidationFn(formFields)(values);
              }}
              initialValues={createInitialValues({
                formFields: updateModalProps.getFormFields(),
                defaultValues: updateModalProps.defaultValues,
              })}
              onSubmit={updateModalProps.onSubmit}
              render={(formikBag) => {
                return (
                  <Form>
                    {updateModalProps.getFormFields(formikBag.values).map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    ))}
                    <SrBox margin="20px 0">
                      <SrButton
                        type="submit"
                        theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                        disabled={isUpdatingItem}
                      >
                        Save
                      </SrButton>
                    </SrBox>
                    <SrButton
                      type="button"
                      theme={SR_BUTTON_THEME_ADOBE_GREY}
                      disabled={isUpdatingItem}
                      onClick={closeModal}
                    >
                      Cancel
                    </SrButton>
                  </Form>
                );
              }}
            />
          </SrModalContent>
        </SrDarkModal>
      )}
    </div>
  );
};

UPAdminAccountingDashboard.propTypes = {
  utilityDealers: PropTypes.array,
  utilitySalesReps: PropTypes.array,
};

UPAdminAccountingDashboard.defaultProps = {
  utilityDealers: [],
  utilitySalesReps: [],
};

export default UPAdminAccountingDashboard;
